/**
 * Kalshi UFC fight props (#848) — the per-bout Kalshi series beside the
 * fight-winner market (KXUFCFIGHT): total rounds, method of victory, method of
 * finish, round of victory and goes-the-distance.
 *
 * Contract: docs/261008KalshiUfcFightProps.md.
 *
 * Producer: hydracore docs/kalshi-ufc-fight-props.md (class `fight-prop`,
 * per-series `stat`, the bout's KXUFCFIGHT key, terms `fighter` /
 * `fighterName` / `method` / `round` / `line`). The suffix grammar here
 * mirrors Core's `deriveFightPropCanonicals` exactly, so both sides accept
 * and refuse the same tickers.
 *
 * Identity rules this module owns (pure, no store access):
 *  - FAMILY comes from the Kalshi series token, matched EXACTLY (the whole
 *    first ticker segment), never by prefix and never from a title. A wire
 *    `class`, `stat` or `seriesTicker` that disagrees with the series
 *    token refuses the row.
 *  - TERMS (round line, method, round, fighter) come from the closed suffix
 *    grammar, because the UFC edge does not forward Core's explicit terms
 *    yet. Any explicit term that IS on the wire must equal the derived one.
 *  - The BOUT is the fight's shared Kalshi event code (`26OCT10ALLDUN`).
 *    Whether a row is admitted to a G1 game is the store selector's decision
 *    (`lib/ufc/fightPropSelection`): it requires the producer's canonical
 *    key AND that exact event code.
 *  - The FIGHTER on a sided leg (method of victory, round of victory) is the
 *    leg suffix's fighter code, accepted only when it equals EXACTLY ONE of
 *    the bout's own KXUFCFIGHT outcome codes. Two candidate splits, no
 *    candidate, or an unknown method token → null (refuse; never guess).
 *
 * Kalshi-only: Polymarket lists no counterpart for these families.
 */

export type UfcFightPropFamily =
  | 'rounds'
  | 'method-of-victory'
  | 'method-of-finish'
  | 'round-of-victory'
  | 'distance';

/** Venue method tokens (ticker suffix) → G1 method id. `draw` is Kalshi's
 *  "Draw/No Contest" leg. */
export type UfcMethod = 'ko' | 'sub' | 'dec' | 'draw';

/** Exact series token → family. Five entries; nothing else is a fight prop. */
export const UFC_FIGHT_PROP_SERIES: ReadonlyMap<string, UfcFightPropFamily> = new Map([
  ['KXUFCROUNDS', 'rounds'],
  ['KXUFCMOV', 'method-of-victory'],
  ['KXUFCMOF', 'method-of-finish'],
  ['KXUFCVICROUND', 'round-of-victory'],
  ['KXUFCDISTANCE', 'distance'],
]);

/** The producer's row class for all five series (hydracore
 *  docs/kalshi-ufc-fight-props.md). */
export const UFC_FIGHT_PROP_CLASS = 'fight-prop';

/** The producer's per-series `stat` (Hydra Core, feat/kalshi-ufc-fight-props).
 *  When a row carries `stat`, it must be this value for its series. */
export const UFC_FIGHT_PROP_STAT: Readonly<Record<UfcFightPropFamily, string>> = {
  rounds: 'rounds-before',
  'method-of-victory': 'method-of-victory',
  'method-of-finish': 'method-of-finish',
  'round-of-victory': 'round-of-victory',
  distance: 'distance',
};

/** Render order of the families on the Fight Props section. */
export const UFC_FIGHT_PROP_FAMILIES: readonly UfcFightPropFamily[] = [
  'rounds', 'method-of-victory', 'method-of-finish', 'round-of-victory', 'distance',
];

export const UFC_FIGHT_PROP_FAMILY_LABEL: Readonly<Record<UfcFightPropFamily, string>> = {
  rounds: 'Total rounds',
  'method-of-victory': 'Method of victory',
  'method-of-finish': 'Method of finish',
  'round-of-victory': 'Round of victory',
  distance: 'Goes the distance',
};

/** Method token → id, longest token first (no token is a suffix of another,
 *  but keep the discipline). */
const METHOD_TOKENS: ReadonlyArray<[string, Exclude<UfcMethod, 'draw'>]> = [
  ['KOTKODQ', 'ko'],
  ['SUB', 'sub'],
  ['DEC', 'dec'],
];

/** G1 method id → the producer's `method` value. The round-of-victory
 *  `OTHER` leg's value is `decision-draw-nc` (see `ufcFightPropExpectedTerms`). */
const WIRE_METHOD: Readonly<Record<UfcMethod, string>> = {
  ko: 'ko-tko-dq',
  sub: 'submission',
  dec: 'decision',
  draw: 'draw-nc',
};

/** Display order of methods inside a family (Kalshi's own listing order). */
export const UFC_METHOD_ORDER: readonly UfcMethod[] = ['ko', 'sub', 'dec', 'draw'];

export const UFC_METHOD_LABEL: Readonly<Record<UfcMethod, string>> = {
  ko: 'KO/TKO/DQ',
  sub: 'Submission',
  dec: 'Decision',
  draw: 'Draw/No Contest',
};

/** Highest round a Kalshi UFC prop can name (five-round bouts). */
export const UFC_MAX_ROUND = 5;

const MONTHS = 'JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC';
const EVENT_CODE_RE = new RegExp(`^\\d{2}(?:${MONTHS})\\d{2}[A-Z0-9]+$`);
const SEGMENT_RE = /^[A-Z0-9]+$/;

export interface UfcFightPropTicker {
  series: string;
  family: UfcFightPropFamily;
  /** The bout's shared Kalshi event code, e.g. `26OCT10ALLDUN`. */
  eventCode: string;
  /** The leg suffix, e.g. `3`, `DUNSUB`, `SUB`, `DUN3`, `OTHER`, `DIST`. */
  suffix: string;
}

/** Parse a ticker into its fight-prop family + bout event code, or null when
 *  it is not exactly `<one of the five series>-<event code>-<leg>`. */
export function parseUfcFightPropTicker(ticker: string): UfcFightPropTicker | null {
  if (typeof ticker !== 'string') return null;
  const parts = ticker.split('-');
  if (parts.length !== 3) return null;
  const [series, eventCode, suffix] = parts as [string, string, string];
  const family = UFC_FIGHT_PROP_SERIES.get(series);
  if (!family) return null;
  if (!EVENT_CODE_RE.test(eventCode) || !SEGMENT_RE.test(suffix)) return null;
  return { series, family, eventCode, suffix };
}

/** The bout event code of a KXUFCFIGHT (winner) ticker, or null. */
export function ufcFightEventCode(ticker: string): string | null {
  const parts = ticker.split('-');
  if (parts.length < 2 || parts[0] !== 'KXUFCFIGHT') return null;
  return EVENT_CODE_RE.test(parts[1] ?? '') ? parts[1]! : null;
}

/** One resolved leg. `fighterCode` is the bout's own KXUFCFIGHT outcome code
 *  (never a guess); null on the unsided legs (draw / OTHER). */
export type UfcFightPropLeg =
  | { family: 'rounds'; round: number }
  | { family: 'distance' }
  | { family: 'method-of-finish'; method: UfcMethod }
  | { family: 'method-of-victory'; method: UfcMethod; fighterCode: string | null }
  /** `round` null = Kalshi's `OTHER` leg (decision, draw or no contest). */
  | { family: 'round-of-victory'; round: number | null; fighterCode: string | null };

const parseRound = (s: string): number | null => {
  if (!/^[1-9]\d?$/.test(s)) return null;
  const n = Number(s);
  return n >= 1 && n <= UFC_MAX_ROUND ? n : null;
};

/**
 * Resolve a parsed leg against the bout's two KXUFCFIGHT outcome codes.
 * Null when the suffix does not fit its family's grammar, names a method
 * token G1 does not know, or splits onto zero / both fighters.
 */
export function resolveUfcFightPropLeg(
  t: UfcFightPropTicker,
  fighterCodes: readonly string[],
): UfcFightPropLeg | null {
  const { family, suffix } = t;
  switch (family) {
    case 'rounds': {
      // "Fight ends before round N" — N = 1 can never resolve YES; Kalshi
      // lists 2..5. Anything else is not a round line.
      const round = parseRound(suffix);
      return round != null && round >= 2 ? { family, round } : null;
    }
    case 'distance':
      return suffix === 'DIST' ? { family } : null;
    case 'method-of-finish': {
      if (suffix === 'DRAW') return { family, method: 'draw' };
      const hit = METHOD_TOKENS.find(([tok]) => tok === suffix);
      return hit ? { family, method: hit[1] } : null;
    }
    case 'method-of-victory': {
      if (suffix === 'DRAW') return { family, method: 'draw', fighterCode: null };
      const candidates: Array<{ fighterCode: string; method: UfcMethod }> = [];
      for (const code of distinctCodes(fighterCodes)) {
        if (!suffix.startsWith(code)) continue;
        const rest = suffix.slice(code.length);
        const hit = METHOD_TOKENS.find(([tok]) => tok === rest);
        if (hit) candidates.push({ fighterCode: code, method: hit[1] });
      }
      return candidates.length === 1 ? { family, ...candidates[0]! } : null;
    }
    case 'round-of-victory': {
      if (suffix === 'OTHER') return { family, round: null, fighterCode: null };
      const candidates: Array<{ fighterCode: string; round: number }> = [];
      for (const code of distinctCodes(fighterCodes)) {
        if (!suffix.startsWith(code)) continue;
        const round = parseRound(suffix.slice(code.length));
        if (round != null) candidates.push({ fighterCode: code, round });
      }
      return candidates.length === 1 ? { family, ...candidates[0]! } : null;
    }
  }
}

function distinctCodes(codes: readonly string[]): string[] {
  const out = new Set<string>();
  for (const c of codes) {
    const u = c?.trim().toUpperCase();
    if (u) out.add(u);
  }
  return [...out];
}

/** Wire fields that, when present, must agree with the ticker's series. A row
 *  whose producer `class`, `stat` or `seriesTicker` names another family is
 *  refused — the two authorities disagree, so neither is trusted. */
export function ufcFightPropWireAgrees(
  t: UfcFightPropTicker,
  wire: { class?: string | null; stat?: string | null; seriesTicker?: string | null },
): boolean {
  const cls = wire.class?.trim().toLowerCase();
  if (cls && cls !== UFC_FIGHT_PROP_CLASS) return false;
  const stat = wire.stat?.trim().toLowerCase();
  if (stat && stat !== UFC_FIGHT_PROP_STAT[t.family]) return false;
  const series = wire.seriesTicker?.trim().toUpperCase();
  if (series && series !== t.series) return false;
  return true;
}

/** The producer's explicit prop terms for a leg, as derived from the ticker:
 *  what `method` / `round` / `line` must read, and whether a `fighter` may be
 *  present. `undefined` = the field must be absent. */
export function ufcFightPropExpectedTerms(leg: UfcFightPropLeg): {
  method: string | undefined;
  round: number | undefined;
  line: number | undefined;
  sided: boolean;
} {
  switch (leg.family) {
    case 'rounds':
      return { method: undefined, round: undefined, line: leg.round, sided: false };
    case 'distance':
      return { method: undefined, round: undefined, line: undefined, sided: false };
    case 'method-of-finish':
      return { method: WIRE_METHOD[leg.method], round: undefined, line: undefined, sided: false };
    case 'method-of-victory':
      return { method: WIRE_METHOD[leg.method], round: undefined, line: undefined, sided: leg.fighterCode != null };
    case 'round-of-victory':
      return leg.round == null
        ? { method: 'decision-draw-nc', round: undefined, line: undefined, sided: false }
        : { method: undefined, round: leg.round, line: undefined, sided: true };
  }
}

/** How a bout's codes map onto the producer's canonical pair: the board
 *  side of each KXUFCFIGHT outcome code (`ALL` → away), and the canonical
 *  away/home surname codes (`Allen` / `Duncan`), one end of the key pair
 *  each. */
export interface UfcFightPropSides {
  sideOfCode: ReadonlyMap<string, 'away' | 'home'>;
  canonical: { away: string; home: string } | null;
}

/** The producer's canonical away/home codes on a prop frame: the explicit
 *  `canonicalAwayCode` / `canonicalHomeCode`, else the two ends of the v2 key
 *  (`ufc|2026-10-10|1|Allen|Duncan`), else of the v1 key. Null when none is
 *  complete. */
export function ufcCanonicalSides(m: {
  canonicalAwayCode?: string | null;
  canonicalHomeCode?: string | null;
  canonicalGameKeyV2?: string | null;
  canonicalGameKey?: string | null;
}): { away: string; home: string } | null {
  const away = m.canonicalAwayCode?.trim();
  const home = m.canonicalHomeCode?.trim();
  if (away && home) return { away, home };
  const v2 = m.canonicalGameKeyV2?.split('|');
  if (v2?.length === 5 && v2[3] && v2[4]) return { away: v2[3], home: v2[4] };
  const v1 = m.canonicalGameKey?.split('|');
  if (v1?.length === 4 && v1[2] && v1[3]) return { away: v1[2], home: v1[3] };
  return null;
}

/**
 * Producer terms (`fighter`, `method`, `round`, `line`) are preferred
 * authorities, but the UFC edge may drop them; the ticker suffix always
 * carries the same terms. Every term that IS on the wire must equal the
 * ticker-derived one — any disagreement (or a term on a leg that has none)
 * refuses the row, so the label a trader reads can never differ from the
 * market the order goes to.
 *
 * `fighter` is the key-pair surname code (hydracore PR #15: "Duncan", one end
 * of the canonical key pair), not an abbreviation of the Kalshi code: Kalshi
 * codes "Rafael dos Anjos" `DOS` and Core keys him `Anjos`, so no prefix rule
 * relates the two. It must equal EXACTLY the canonical code on the leg
 * fighter's own board side, and not the other side's.
 */
export function ufcFightPropTermsAgree(
  leg: UfcFightPropLeg,
  wire: { fighter?: string | null; method?: string | null; round?: number | null; line?: number | null },
  sides: UfcFightPropSides,
): boolean {
  const want = ufcFightPropExpectedTerms(leg);
  const method = wire.method?.trim().toLowerCase();
  if (method && method !== want.method) return false;
  if (wire.round != null && wire.round !== want.round) return false;
  if (wire.line != null && wire.line !== want.line) return false;
  const fighter = wire.fighter?.trim().toUpperCase();
  if (fighter) {
    if (!want.sided) return false;
    const code = 'fighterCode' in leg ? leg.fighterCode : null;
    const side = code ? sides.sideOfCode.get(code.toUpperCase()) : undefined;
    const c = sides.canonical;
    if (!side || !c) return false;
    const own = (side === 'away' ? c.away : c.home).trim().toUpperCase();
    const other = (side === 'away' ? c.home : c.away).trim().toUpperCase();
    if (!own || own === other || fighter !== own) return false;
  }
  return true;
}

/** The fighter pair packed in a bout event code (`26OCT10ALLDUN` → `ALLDUN`). */
export function ufcEventPair(eventCode: string): string {
  return eventCode.slice(7);
}

const MONTH_NUMBER: Readonly<Record<string, string>> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

/** The bout date in a bout event code (`26OCT10ALLDUN` → `2026-10-10`), or
 *  null when the code is not dated. */
export function ufcEventDate(eventCode: string): string | null {
  if (!EVENT_CODE_RE.test(eventCode)) return null;
  const mm = MONTH_NUMBER[eventCode.slice(2, 5)];
  return mm ? `20${eventCode.slice(0, 2)}-${mm}-${eventCode.slice(5, 7)}` : null;
}

/** Every way to cut a packed pair into two non-empty codes, in pack order
 *  (`ALLDUN` → `[A, LLDUN]`, `[AL, LDUN]`, `[ALL, DUN]`, …). Candidates only:
 *  a caller must confirm one against an independent authority. */
export function ufcPairSplits(pair: string): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (let i = 1; i < pair.length; i++) out.push([pair.slice(0, i), pair.slice(i)]);
  return out;
}

/** The leg's short side badge for position / order tables ("DUN by
 *  Submission", "Ends before R3"). Codes, not names: the badge is narrow, and
 *  the matchup label beside it carries the readable names. */
export function ufcFightPropBadge(leg: UfcFightPropLeg): string {
  switch (leg.family) {
    case 'rounds':
      return `Ends before R${leg.round}`;
    case 'distance':
      return 'Goes the distance';
    case 'method-of-finish':
      return leg.method === 'draw'
        ? 'Draw/No Contest (method of finish)'
        : `By ${UFC_METHOD_LABEL[leg.method]}`;
    case 'method-of-victory':
      return leg.fighterCode == null || leg.method === 'draw'
        ? 'Draw/No Contest (method of victory)'
        : `${leg.fighterCode} by ${UFC_METHOD_LABEL[leg.method]}`;
    case 'round-of-victory':
      return leg.fighterCode == null || leg.round == null
        ? 'Decision/Draw/NC'
        : `${leg.fighterCode} in R${leg.round}`;
  }
}

/**
 * The YES proposition in plain words, built from the parsed identity (never
 * from a venue title). `fighterName` resolves the leg's fighter to the game
 * row's display name. These strings are what the order ticket reads as its
 * YES wording ("Fight ends before round 3 — YES").
 */
export function ufcFightPropLabel(leg: UfcFightPropLeg, fighterName: (code: string) => string): string {
  switch (leg.family) {
    case 'rounds':
      return `Fight ends before round ${leg.round}`;
    case 'distance':
      return 'Fight goes the distance';
    // Kalshi lists a draw/no-contest leg in BOTH method families: two
    // separate markets on one proposition. The family tag keeps the two rows
    // (and their tickets) distinguishable.
    case 'method-of-finish':
      return leg.method === 'draw'
        ? 'Fight ends in a draw/no contest (method of finish)'
        : `Fight ends by ${UFC_METHOD_LABEL[leg.method]}`;
    case 'method-of-victory':
      return leg.fighterCode == null || leg.method === 'draw'
        ? 'Fight ends in a draw/no contest (method of victory)'
        : `${fighterName(leg.fighterCode)} wins by ${UFC_METHOD_LABEL[leg.method]}`;
    case 'round-of-victory':
      return leg.fighterCode == null || leg.round == null
        ? 'Fight ends by decision, draw or no contest'
        : `${fighterName(leg.fighterCode)} wins in round ${leg.round}`;
  }
}
